import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { GeneralDialogOkComponent } from 'src/app/core/dialog/general-dialog-ok/general-dialog-ok.component';
import { DataBroadcastService } from 'src/app/core/services/broadcast/data-broadcast.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { ClonerService } from 'src/app/core/services/helper/cloner.service';
import { ParkerStateService } from 'src/app/core/services/state/parker-state.service';
import { ParkerExportTransform } from 'src/app/shared/helpers/transform/parker-export-transform';
import {
  IAdditionalEmails,
  IAdditionalVehicles,
  ILotDisableSpace,
  IParkerProfileDetail,
  IParkerProfileQuery,
  ISpaceEmailUsers,
} from 'src/app/shared/interfaces';
import { ParkerV2SearchCriteriaComponent } from '../parker-v2-search-criteria/parker-v2-search-criteria.component';
import { ParkersV2SearchResultsComponent } from '../parkers-v2-search-results/parkers-v2-search-results.component';
import { NgIf } from '@angular/common';
import { ParkersV2AddNewParkerComponent } from '../parkers-v2-add-new-parker/parkers-v2-add-new-parker.component';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { ParcsDataService } from 'src/app/core/services/data/parcs-data.service';
import { IParcsGetLotConfigurationResponse } from 'src/app/shared/code/interfaces/parcs-interfaces';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-parker-v2-home',
  templateUrl: './parker-v2-home.component.html',
  imports: [ParkerV2SearchCriteriaComponent, ParkersV2SearchResultsComponent, NgIf, ParkersV2AddNewParkerComponent],
  standalone: true,
  styleUrl: './parker-v2-home.component.scss',
  providers: [ParkerStateService, ParcsDataService],
})
export class ParkerV2HomeComponent implements OnInit, OnDestroy {
  parkers: IParkerProfileDetail[] = [];
  filteredParkers: IParkerProfileDetail[] = [];
  additionalEmailList: IAdditionalEmails[] = [];
  additionalVehicleList: IAdditionalVehicles[] = [];
  parkersWithExpiredRates: IParkerProfileDetail[] = [];
  parkersWithDuplicateKeyCards: IParkerProfileDetail[] = [];
  spaceUsersForLotNumber: ISpaceEmailUsers[] = [];
  subscribedData: any;
  spaceDisableLotList: ILotDisableSpace[] = [];

  //Services
  parkerStateService = inject(ParkerStateService);
  dataService = inject(DataService);
  dataBroadcastService = inject(DataBroadcastService);
  clonerService = inject(ClonerService);
  _matDialog = inject(MatDialog);
  _snackBar = inject(MatSnackBar);
  #_appGeneralState = inject(AppGeneralStateService);
  private _parcsDataService = inject(ParcsDataService);
  private _destroyRefService = inject(DestroyRef);

  constructor() {
    this.dataBroadcastService.querySubjectBroadcast.subscribe((res: any) => {
      this.subscribedData = res;
    });
    this.getParcsLotConfigutionByLotNumber(this.#_appGeneralState.currentLotNumber);
  }

  ngOnInit(): void {
    if (this.parkerStateService.parkerEnableMode) {
      this.parkers = this.parkerStateService.getParkerQueryResults();
      this.filteredParkers = this.parkerStateService.getFilteredParkerData();
      this.handleFilterParkerEvent(this.parkerStateService.currentParkerFilter);
    }
  }

  ngOnDestroy(): void {}

  handleLotChangedEvent(newLotNumber: string): void {
    let lotNumber: string = newLotNumber;
    this.parkers = [];
    this.filteredParkers = [];
    this.getParcsLotConfigutionByLotNumber(newLotNumber);
  }

  handleFilterParkerEvent(command: string): void {
    if (command === '') return;

    if (command === 'ALL')
      this.parkerStateService.setFilteredParkerData(
        this.clonerService.deepClone(this.parkerStateService.getParkerQueryResults()),
      );
    else {
      let parkerSubset: IParkerProfileDetail[];
      let allParkersClone: IParkerProfileDetail[];
      allParkersClone = this.clonerService.deepClone(this.parkerStateService.getParkerQueryResults());

      if (command === '0-9') {
        parkerSubset = allParkersClone.filter((option) => !isNaN(Number(option.lastName.charAt(0))));
        this.parkerStateService.setFilteredParkerData(this.clonerService.deepClone(parkerSubset));
      } else {
        parkerSubset = this.parkers.filter((option) => option.lastName.toUpperCase().startsWith(command));
        this.parkerStateService.setFilteredParkerData(this.clonerService.deepClone(parkerSubset));
      }
    }
  }

  GetSpaceDisableLotList() {
    this.dataService.getDisableSpaceList().subscribe(
      (res) => {
        this.spaceDisableLotList = res;
      },
      (err: any) => {},
      () => {
        this.parkerStateService.spaceDisableLotList = this.spaceDisableLotList;
      },
    );
  }

  handleNewQueryEvent(queryData: IParkerProfileQuery): void {
    this.resetParkerQueryResultsData();
    let horizontalPosition: MatSnackBarHorizontalPosition = 'center';
    let verticalPosition: MatSnackBarVerticalPosition = 'top';

    // Reset state variables
    this.parkerStateService.massUpdateResultsExist = false; // Tracks if a user has performed a mass update event
    this.parkerStateService.isEditModeEnabled = false; // If a user has initiated an edit on the parker profile core data this will be true
    this.parkerStateService.currentParkerIdInEditMode = undefined; // If a user has initiated edit mode, this tracks the current parkerId being edited

    this._snackBar.open('Searching for Parkers...', null, {
      horizontalPosition: horizontalPosition,
      verticalPosition: verticalPosition,
    });

    this.GetSpaceDisableLotList();
    this.getAllParkersForLot(queryData);
    this.getAdditionalVehiclesForLot2(queryData.lotNumber);
    this.getAdditionalEmailsForLot(queryData.lotNumber);
    this.getParkersByExpiredRatesByLotNumber(queryData.lotNumber);
    this.getParkersWithDuplicateKeyCardNumbersByLotNumber(queryData.lotNumber);
    this.getSpaceUsersByLotNumber(queryData.lotNumber);
  }

  GetMarketRateListByLotNumber(lotNumber: string) {
    this.dataService.getMarketRatesForLotNumber(lotNumber).subscribe(
      (res) => {
        this.parkerStateService.marketRateList = res;
      },
      (err: any) => {},
      () => {},
    );
  }

  getSpaceUsersByLotNumber(lotNumber: string): void {
    this.dataService.getSpaceUsersByLotNumber(lotNumber).subscribe({
      next: (results) => (this.spaceUsersForLotNumber = results),
      complete: () => {
        this.parkerStateService.spaceUsersForLotNumber = this.spaceUsersForLotNumber;
      },
    });
  }

  getAllParkersForLot(queryData: IParkerProfileQuery): void {
    this.dataService.getParkerListByQueryCriteria(queryData).subscribe({
      next: (results) => (this.parkers = results),
      error: () => {
        this._snackBar.dismiss();
      },
      complete: () => {
        this._snackBar.dismiss();
        if (this.parkers.length == 0) {
          this.DisplayNoSearchResultsDialog();
        }

        this.filteredParkers = this.clonerService.deepClone(this.parkers);
        this.parkerStateService.queryNumberOfParkerResults = this.parkers.length;
        this.parkerStateService.setParkerQueryResults(this.clonerService.deepClone(this.parkers));
        this.parkerStateService.setFilteredParkerData(this.clonerService.deepClone(this.filteredParkers));
        this.parkerStateService.setEnableModeOn();
        this.parkerStateService.parkerQueryPerformed = true;

        //This sets the data used for the export tab
        let parkerTransform = new ParkerExportTransform(this.parkerStateService);

        this.parkerStateService.gridExportData = parkerTransform.TransformRawParkerList(
          this.parkerStateService.getFilteredParkerData(),
        );
      },
    });
  }

  DisplayNoSearchResultsDialog(): void {
    const dlgTitle: string = 'No Parkers Found';
    const dlgLine1: string = 'No parkers were found matching this search criteria.';
    const dlgLine2: string = '';
    let enterAnimationDuration: string = '0ms';
    let exitAnimationDuration: string = '0ms';

    const dialogRef = this._matDialog.open(GeneralDialogOkComponent, {
      data: { dlgTitle: dlgTitle, dlgLine1: dlgLine1, dlgLine2: dlgLine2 },
      width: '450px',
      enterAnimationDuration,
      exitAnimationDuration,
    });
  }

  resetParkerQueryResultsData(): void {
    this.filteredParkers = [];
    this.parkerStateService.filteredParkers = [];
    this.parkerStateService.parkerQueryResults = [];
    this.parkers = [];
  }

  getParkersByExpiredRatesByLotNumber(lotNumber: string): void {
    this.dataService.getParkersExpiredRatesByLotNumber(lotNumber).subscribe({
      next: (results) => (this.parkersWithExpiredRates = results),
      complete: () => {
        this.parkerStateService.parkerExpiredRates = this.parkersWithExpiredRates;
      },
    });
  }

  getParkersWithDuplicateKeyCardNumbersByLotNumber(lotNumber: string): void {
    this.dataService.GetParkersDuplicateKeyCardNumbersForLot(lotNumber).subscribe({
      next: (results) => (this.parkersWithDuplicateKeyCards = results),
      complete: () => {
        this.parkerStateService.parkerDuplicateKeyCards = this.parkersWithDuplicateKeyCards;
      },
    });
  }

  getAdditionalEmailsForLot(lotNumber: string): void {
    this.dataService.getAdditionalEmailsForLotNumber(lotNumber).subscribe({
      next: (results) => (this.additionalEmailList = results),
      complete: () => {
        this.parkerStateService.setAdditionalEmailData(this.additionalEmailList);
      },
    });
  }

  getAdditionalVehiclesForLot(lotNumber: string): void {
    this.dataService
      .getAdditionalVehiclesForLotNumber(lotNumber)
      .subscribe((data: IAdditionalVehicles[]) => (this.additionalVehicleList = data));
  }

  getAdditionalVehiclesForLot2(lotNumber: string): void {
    this.dataService.getAdditionalVehiclesForLotNumber(lotNumber).subscribe({
      next: (results) => (this.additionalVehicleList = results),
      complete: () => {
        this.parkerStateService.setAdditionalVehicleData(this.additionalVehicleList);
      },
    });
  }

  getParcsLotConfigutionByLotNumber(lotNumber: string) {
    this._parcsDataService
      .getParcsLotConfigurationByLotNumber(lotNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.#_appGeneralState.currentLotParcsConfiguration = res;
        },
        error: (err: any) => {
          this.#_appGeneralState.currentLotParcsConfiguration = {} as IParcsGetLotConfigurationResponse;
        },
        complete: () => {},
      });
  }
}
